import { AsyncButton, Button } from '@components/button';
import { Form, FormFileUpload } from '@components/form';
import { useFileUpload } from '@fileUpload/FileUpload';
import {
  useUploadVolumeDataFinalizerMutation,
  useUploadVolumeDataMutation,
} from '@generated/graphql';
import { runPromiseQueue } from '@helper-functions/promiseQueue';
import { Stack } from '@mui/material';
import { useState } from 'react';
import { BehaviorSubject } from 'rxjs';

interface VolumeUploadModalProps {
  directory: string[];
  onClose: () => void;
  onFileUploaded: () => void;
  volumeId: string;
  uploadPercentages$: BehaviorSubject<Record<string, number>>;
  fileNames$: BehaviorSubject<string[]>;
  open$: BehaviorSubject<boolean>;
}

export const VolumeUploadModal = ({
  onClose,
  onFileUploaded,
  volumeId,
  directory,
  uploadPercentages$,
  fileNames$,
  open$,
}: VolumeUploadModalProps) => {
  const [isUploadStarted, setIsUploadStarted] = useState<boolean>(false);

  const [uploadVolumeData] = useUploadVolumeDataMutation();
  const [uploadVolumeDataFinalizer] = useUploadVolumeDataFinalizerMutation();
  const uploadFile = useFileUpload();

  const uploadSingleFile = async (file: File) => {
    const { data } = await uploadVolumeData({
      variables: {
        volumeId,
        key: `${directory.join('')}${file.name}`,
        size: file.size,
      },
    });

    open$.next(true);
    uploadPercentages$.next({ ...uploadPercentages$.value, [file.name]: 0 });
    fileNames$.next([...fileNames$.value, file.name]);

    const etags = await uploadFile({
      file,
      urls: data?.uploadVolumeData?.urls,
      partSize: data?.uploadVolumeData?.partSize,
      onUploadProgress: (percent: number) => {
        uploadPercentages$.next({
          ...uploadPercentages$.value,
          [file.name]: Math.round(percent * 100),
        });
      },
    });

    await uploadVolumeDataFinalizer({
      variables: {
        parts: etags,
        key: data?.uploadVolumeData?.key,
        uploadId: data?.uploadVolumeData?.uploadId,
      },
    });
    onFileUploaded();
    onClose();
  };

  const uploadVolumeAsyncFunction = async (files: File[]) => {
    if (files.length === 0) {
      return;
    }
    const promise = runPromiseQueue(
      files.map((file) => {
        return () => uploadSingleFile(file);
      }),
      3,
    );
    onClose();
    await promise;
    setTimeout(() => {
      uploadPercentages$.next({});
      fileNames$.next([]);
      open$.next(false);
    }, 5000);
  };

  const submitVolume = async ({ files }: { files: File[] }) => {
    setIsUploadStarted(true);
    await uploadVolumeAsyncFunction(files);
    setIsUploadStarted(false);
  };

  return (
    <Form onSubmit={submitVolume} initialValues={{ files: [] }}>
      {({ handleSubmit, values }) => (
        <Stack>
          <FormFileUpload name="files" label="Add Files" multiple />
          <Stack gap={4}>
            <AsyncButton
              fullWidth
              type="submit"
              disabled={values.files.length === 0 || isUploadStarted}
              onClick={handleSubmit}
              data-cy="Volume-Upload-Modal-Upload-Button"
            >
              Upload
            </AsyncButton>
            <Button
              fullWidth
              variant="secondary"
              data-cy="Volume-Upload-Modal-Cancel-Button"
              onClick={onClose}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </Form>
  );
};
